import React, { useEffect, useMemo, useState } from "react";
import { Search, Crown } from "lucide-react";
import Sidebar from "../Dashboard/Sidebar";
import "../Dashboard/Dashboard.css";
import "./Leaderboard.css";
import { tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { getActiveTrackId } from "../../utils/session";
import { useAuth } from "../../context/AuthContext";
import { getGenderAvatarUrl, getLeaderboardEntryGender } from "../../utils/avatar";

const Leaderboard = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      setError("");
      try {
        const activeTrackId = getActiveTrackId();
        const response = activeTrackId
          ? await tracksApi.trackLeaderboard(activeTrackId)
          : await tracksApi.globalLeaderboard();

        const rows = response.data?.leaderboard || [];
        setAllRows(rows);
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load leaderboard"));
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => row.fullName?.toLowerCase().includes(q));
  }, [search, allRows]);

  const topThree = filteredRows.slice(0, 3);
  const rest = filteredRows.slice(3);
  const hasRestRows = rest.length > 0;

  const getRiskByRank = (rank) => {
    if (rank <= 3) return "Low Risk";
    if (rank <= 8) return "Medium Risk";
    return "High Risk";
  };

  const currentUserRow = filteredRows.find((item) => String(item.studentId) === String(user?.id || user?._id));

  const getRiskClass = (risk) => {
    switch (risk) {
      case "Low Risk":
        return "badge-low-risk";
      case "Medium Risk":
        return "badge-medium-risk";
      case "High Risk":
        return "badge-high-risk";
      default:
        return "";
    }
  };

  return (
    <div className="dashboard-container ">
      <div className="dashboard-sidebar-wrapper">
        <Sidebar />
      </div>
      <div className="dashboard-main-wrapper leaderboard-wrapper">
        <header className="lb-header">
          <h1 className="lb-title">Global Leaderboard</h1>
        </header>

        <div className="filters-bar pb-5">
          <div className="search-box">
            <Search className="search-icon text-muted" size={18} />
            <input
              type="text"
              className="search-input"
              placeholder="Search for a student..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? <div className="alert alert-info py-2">Loading leaderboard...</div> : null}
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        {!loading && !filteredRows.length ? (
          <div className="alert alert-light border">No leaderboard entries found.</div>
        ) : (
          <>
            <div className="podium-grid mb-5">
              {topThree.map((userRow, index) => {
                const rank = userRow.rank || index + 1;
                const avatarUrl = getGenderAvatarUrl(
                  getLeaderboardEntryGender(userRow, String(user?.id || user?._id) === String(userRow.studentId) ? user?.gender : "")
                );
                return (
                  <div key={userRow.studentId} className={`podium-card ${rank === 1 ? "featured-card" : ""}`}>
                    <div className="podium-avatar-wrapper">
                      <img
                        src={avatarUrl}
                        alt={userRow.fullName}
                        className={`podium-img ${rank === 1 ? "large-img" : ""}`}
                      />
                      <span className={`podium-rank-badge rank-${rank}`}>{rank}</span>
                      {rank === 1 && (
                        <div className="podium-crown" aria-hidden="true">
                          <Crown size={18} />
                        </div>
                      )}
                    </div>

                    <h4 className="podium-name">{userRow.fullName}</h4>
                    <p className="podium-track">Student</p>

                    <div className="podium-points text-blue">
                      <h2 className="points-val">{userRow.points}</h2>
                      <small className="points-lbl">TOTAL POINTS</small>
                    </div>
                  </div>
                );
              })}
            </div>

            {hasRestRows ? (
              <div className="rankings-table-card">
                <div className="rankings-table-scroll">
                  <table className="table custom-lb-table mb-0">
                    <thead>
                      <tr>
                        <th className="ps-4">RANK</th>
                        <th>STUDENT</th>
                        <th className="text-center">TOTAL POINTS</th>
                        <th className="pe-4">RISK</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rest.map((row) => {
                        const risk = getRiskByRank(row.rank);
                        const avatarUrl = getGenderAvatarUrl(
                          getLeaderboardEntryGender(row, String(user?.id || user?._id) === String(row.studentId) ? user?.gender : "")
                        );
                        return (
                          <tr key={row.studentId}>
                            <td className="ps-4 text-muted fw-bold">{row.rank}</td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <img src={avatarUrl} alt={row.fullName} className="table-avatar" />
                                <span className="fw-bold text-dark">{row.fullName}</span>
                              </div>
                            </td>
                            <td className="text-center fw-bold text-dark">{row.points}</td>
                            <td className="pe-4">
                              <span className={`risk-badge ${getRiskClass(risk)}`}>
                                <span className="risk-dot"></span> {risk}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="rankings-table-empty">
                All ranked members are already shown in the top cards.
              </div>
            )}

            {currentUserRow ? (
              <div className="your-rank-sticky shadow-lg">
                <div className="yr-left">
                  <div className="yr-rank-box text-dark">
                    <span className="small text-muted fw-bold">RANK</span>
                    <span className="fs-5 fw-bold ms-2">{currentUserRow.rank}</span>
                  </div>
                  <div className="d-flex align-items-center gap-3 ms-4">
                    <img src={getGenderAvatarUrl(getLeaderboardEntryGender(currentUserRow, user?.gender))} alt="You" className="table-avatar rounded-circle" />
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-dark">You</span>
                      <span className="small text-muted">Keep learning to climb!</span>
                    </div>
                  </div>
                </div>
                <div className="yr-right">
                  <div className="d-flex flex-column align-items-end me-4">
                    <span className="small text-muted">Total Points</span>
                    <span className="fs-5 fw-bold text-blue">{currentUserRow.points}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
